import React from "react";
import styles from "../stylesERv2.module.scss";

const mesesAbrev = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const mostrarFecha = (fechaStr) => {
  if (!fechaStr) return "Sin fecha";
  const [, month, day] = fechaStr.split("-");
  return `${day}-${mesesAbrev[parseInt(month) - 1]}`;
};

/**
 * Detalle de ingresos del header "B - Hardware" del ER v2 para un mes. Solo lectura.
 *
 * Mismas columnas e info que el HardwareIngresosModal del v1 (ID / Cliente-Edificio /
 * Pantallas / Modalidad / Cuota-Monto / Fecha factura / Valor UF|Dólar / Total), con el
 * maquetado del ER v2. NO porta la edición de fechas ni el refetch async del v1: los negocios
 * del mes (`negocios`, con `_cuotasEnMes` / `_facturaFecha` / `_montoTramoMes` ya resueltos por
 * el devengado) llegan calculados desde index.js.
 *
 * El total al pie es `ingresosHardwareTotal` = ingresosHardwareMensual[monthIndex] (el mismo
 * agregado que muestra la celda del ER, igual que el v1): cierra EXACTO con la celda por
 * construcción. Las filas son el desglose por negocio del devengado de ese mes.
 */
export const DetalleHardwareIngresosModal = ({
  isOpen,
  monthIndex,
  selectedYear,
  currentYear,
  mesesCompletos,
  pais,
  moneda,
  localString,
  formatCurrency,
  getNombreNegocio,
  getFechaFacturaHardware,
  getDolarValue,
  getUFValue,
  convertirAMonedaLocal,
  costosGestionData,
  negocios = [],
  ingresosHardwareTotal = 0,
  onClose,
}) => {
  if (!isOpen || monthIndex === null || monthIndex === undefined) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContentComisionComercial} onClick={(e) => e.stopPropagation()}>
        <h3>
          Ingresos Hardware — {mesesCompletos[monthIndex]} {selectedYear}
        </h3>
        <div style={{ overflowX: "auto" }}>
          <table className={styles.modalTable}>
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>ID</th>
                <th style={{ textAlign: "left" }}>Cliente / Edificio</th>
                <th style={{ textAlign: "center" }}>Pantallas</th>
                <th style={{ textAlign: "center" }}>Modalidad</th>
                <th style={{ textAlign: "end" }}>Cuota / Monto</th>
                <th style={{ textAlign: "center" }}>Fecha factura</th>
                {pais === "Perú" && <th style={{ textAlign: "center" }}>Valor Dólar</th>}
                {pais === "Chile" && <th style={{ textAlign: "center" }}>Valor UF</th>}
                <th style={{ textAlign: "end" }}>Total {moneda(pais).trim()}</th>
              </tr>
            </thead>
            <tbody>
              {negocios.length === 0 ? (
                <tr>
                  <td
                    colSpan={pais === "Chile" || pais === "Perú" ? 8 : 7}
                    style={{ textAlign: "center", color: "#666" }}
                  >
                    No hay ingresos de hardware en este mes.
                  </td>
                </tr>
              ) : (
                negocios.map((n) => {
                  const nombre = getNombreNegocio(n);
                  const pantallas = Number(n.cantidad_pantallas) || 1;
                  const monedaHw = (n.moneda_hardware || "UF").toUpperCase();
                  const cuotasEnMes = n._cuotasEnMes || 1;
                  const fechaUsada = n._facturaFecha || getFechaFacturaHardware(n, monthIndex);
                  const registro = costosGestionData.find(
                    (c) =>
                      Number(c.negocio_id) === Number(n.id) &&
                      Number(c.mes) === monthIndex + 1 &&
                      Number(c.ano) === currentYear
                  );
                  const fechaFacturaGuardada = n._facturaFecha || registro?.fecha_factura?.split("T")[0] || null;

                  const isUSD = monedaHw === "US$" || monedaHw === "USD";
                  const valorReferencia = isUSD
                    ? getDolarValue(fechaUsada)
                    : pais === "Chile" && monedaHw === "UF"
                      ? getUFValue(fechaUsada)
                      : null;
                  // Columna "Valor Dólar" (Perú) / "Valor UF" (Chile): string ya formateado.
                  let valorRefStr = "-";
                  if (pais === "Perú" && isUSD) {
                    valorRefStr = valorReferencia.toLocaleString("es-PE", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });
                  } else if (pais === "Chile" && valorReferencia) {
                    valorRefStr = valorReferencia.toLocaleString("es-CL", { maximumFractionDigits: 0 });
                  }

                  // CAPEX con desglose: monto del/los tramo(s) del mes (_montoTramoMes) para reconciliar
                  // con la celda "B - Hardware" (10.229). Sin desglose → monto_hardware entero (como el v1).
                  const montoOriginal =
                    n.modalidad === "OPEX"
                      ? Number(n.cuota_hardware_opex) * pantallas * cuotasEnMes
                      : n._montoTramoMes != null
                        ? Number(n._montoTramoMes)
                        : Number(n.monto_hardware) || 0;

                  const totalLocal = convertirAMonedaLocal(
                    montoOriginal,
                    monedaHw,
                    pais,
                    n.id,
                    monthIndex + 1,
                    selectedYear,
                    fechaUsada
                  );

                  return (
                    <tr key={n.id}>
                      <td style={{ textAlign: "center", fontWeight: 600 }}>{n.id}</td>
                      <td style={{ textAlign: "left" }}>{nombre}</td>
                      <td style={{ textAlign: "center" }}>{pantallas}</td>
                      <td style={{ textAlign: "center" }}>{n.modalidad}</td>
                      <td style={{ textAlign: "end" }}>
                        {montoOriginal.toLocaleString(localString(pais), {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        {monedaHw}
                      </td>
                      <td style={{ textAlign: "center" }}>{mostrarFecha(fechaFacturaGuardada)}</td>
                      {(pais === "Perú" || pais === "Chile") && <td style={{ textAlign: "center" }}>{valorRefStr}</td>}
                      <td style={{ textAlign: "end" }}>{formatCurrency(totalLocal, monthIndex)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={pais === "Chile" || pais === "Perú" ? 7 : 6} style={{ textAlign: "end", fontWeight: 600 }}>
                  Total
                </td>
                <td style={{ textAlign: "end", fontWeight: 600 }}>
                  {formatCurrency(ingresosHardwareTotal, monthIndex)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className={styles.modalActions}>
          <button className={styles.btnClose} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
